<template>
  <div v-show="recommendedProducts?.length" class="overflow-hidden" :class="brandClasses.container">
    <h2 v-if="content?.title" class="mb-4 title-2 lg:mb-6">
      <span v-if="$feature.showTitleHtml" v-html="content.titleHtml" />
      <span v-else>{{ content.title }}</span>
    </h2>
    <client-only>
      <vf-carousel
        v-if="recommendedProducts?.length"
        class="-mx-2"
        :class="brandClasses.carouselContainer"
        :class-controls="['-mt-9 mx-2', brandClasses.carouselControls]"
        loop
      >
        <div
          v-for="(product, i) in recommendedProducts"
          :key="`${product.id}-${i}`"
          :class="[variantStyleMap[content.variant!], brandClasses.productContainer]"
          @click="handleProductClick(product, i + 1)"
        >
          <product-card-recommended v-bind="product" :currency="currency[locale]" :gtm-config="{ list, category: list }" :quickshop />
        </div>
      </vf-carousel>
      <template #fallback>
        <div
          class="grid gap-4 children:skeleton"
          :class="variantStyleMap[content.variant!]"
        >
          <template v-for="i in content.variant === '3-cards' ? 1 : 2" :key="i">
            <div class="aspect-square" />
            <div class="aspect-square <md:hidden" />
            <div class="aspect-square <lg:hidden" />
          </template>
        </div>
      </template>
    </client-only>
  </div>
</template>

<script lang="ts" setup>
import type { ProductRecommendations } from '#types/components/cms/product-recommendations'
import type { MonetateExtractedProduct } from '#types/p13n'
import { getPageInfo } from '#commerce/gtm/page'

const props = defineProps<{
  content: ProductRecommendations
}>()

const { brandClasses, enableQuickShop } = useAppConfig().components.cms.productRecommendations
const { currency } = useRuntimeConfig().public
const { sendMonetateEvents, sendRecImpressionsEvent, extractProduct } = useMonetate()
const { $gtm } = useNuxtApp()
const locale = useLocale()
const route = useRoute()
const [pageCategory] = getPageInfo(route, {})

const variantStyleMap: Record<NonNullable<ProductRecommendations['variant']>, string> = {
  '3-cards': 'w-full md:w-1/2 lg:w-1/3',
  '5-cards': 'w-1/2 md:w-1/4 lg:w-1/5',
  '6-cards': 'w-1/2 md:w-1/4 lg:w-1/6',
}

const quickshop = props.content.quickShopVisibility ? props.content.quickShopVisibility === 'visible' : enableQuickShop

const recommendedProducts = computed<MonetateExtractedProduct[]>(() => props.content.recommendedProducts?.map(
  (product) => (extractProduct(product, props.content.showRatings))
))

const list = getRecommendationListDescription({ list: `${pageCategory || 'Home'} | Product Recommendation Carousel`, title: props.content.title })

const handleProductClick = (product, position: number) => {
  const [pageCategory] = getPageInfo(route, {})
  sendMonetateEvents([{
    eventType: 'monetate:record:RecClicks',
    recClicks: [product.recToken]
  }])

  $gtm.push('product.onProductRecClick', product, position, {
    action: 'Click Recommendation',
    currency: currency[locale],
    experience: props.content.monetateDecision,
    list: `${pageCategory || 'Home'} | Product Recommendation Carousel`,
    title: props.content.title,
    vendor: 'monetate'
  })
}

whenever(() => (props.content.monetateDecision?.name !== 'Control' && props.content.recommendedProducts?.length && props.content.selected), () => {
  const [pageCategory] = getPageInfo(route, {})
  sendRecImpressionsEvent(props.content.recommendedProducts, 24)
  $gtm.push('product.onProductRecImpressions', props.content.recommendedProducts, {
    currency: currency[locale],
    experience: props.content.monetateDecision,
    list: `${pageCategory || 'Home'} | Product Recommendation Carousel`,
    title: props.content.title,
    vendor: 'monetate'
  })
}, { immediate: true, once: true })
</script>
